import React from 'react';
import { Home as HomeIcon, Building2, Briefcase, CheckCircle, ArrowRight, ClipboardList, Snowflake, Wrench, Hammer, Shield, Package, RefreshCw, Truck, Sparkles, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { services } from '../data/mock';

const Services = () => {
  const iconMap = {
    home: HomeIcon,
    building: Building2,
    briefcase: Briefcase,
    clipboardList: ClipboardList,
    snowflake: Snowflake,
    wrench: Wrench,
    hammer: Hammer,
    shield: Shield,
    package: Package,
    refresh: RefreshCw,
    truck: Truck,
    sparkles: Sparkles,
    trash: Trash2
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brand-blue-50 via-blue-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            #1 Cleaning Services in Winnipeg, Manitoba
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
            Professional deep clean, office clean, janitorial services, commercial cleaning, post construction clean, waste management, property management, and live-in caretaker services throughout Winnipeg, Manitoba
          </p>
          <p className="text-lg text-gray-700 max-w-4xl mx-auto mb-8">
            <strong>Serving Winnipeg:</strong> Deep cleaning services, office cleaning, janitorial services, commercial property cleaning, post-construction cleanup, waste dump and removal, residential cleaning, carpet cleaning, property manager services, and professional live-in caretakers
          </p>
          <Link to="/book">
            <Button size="lg" className="bg-brand-blue-600 hover:bg-brand-blue-700 text-white">
              Get Your Free Quote - Call 204-294-6802
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* SEO-Rich Winnipeg Keywords Section */}
      <section className="py-12 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Complete Cleaning & Property Care Solutions in Winnipeg
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              From deep clean services to property management, we're Winnipeg's most trusted cleaning company serving residential, commercial, and industrial properties throughout Manitoba
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-brand-blue-50 p-6 rounded-lg border-2 border-brand-blue-200 hover:border-brand-gold transition-all">
              <h3 className="text-xl font-bold text-brand-navy mb-3">Deep Clean Winnipeg</h3>
              <p className="text-gray-700">Professional deep cleaning services for homes, offices, and commercial spaces. Thorough, detailed cleaning that goes beyond surface level.</p>
            </div>
            
            <div className="bg-brand-blue-50 p-6 rounded-lg border-2 border-brand-blue-200 hover:border-brand-gold transition-all">
              <h3 className="text-xl font-bold text-brand-navy mb-3">Office Clean Winnipeg</h3>
              <p className="text-gray-700">Daily office cleaning, desk sanitization, and workspace maintenance for businesses throughout Winnipeg and surrounding areas.</p>
            </div>
            
            <div className="bg-brand-blue-50 p-6 rounded-lg border-2 border-brand-blue-200 hover:border-brand-gold transition-all">
              <h3 className="text-xl font-bold text-brand-navy mb-3">Janitorial Services Winnipeg</h3>
              <p className="text-gray-700">Complete janitorial services for commercial buildings, offices, warehouses, and retail spaces with flexible scheduling.</p>
            </div>
            
            <div className="bg-brand-blue-50 p-6 rounded-lg border-2 border-brand-blue-200 hover:border-brand-gold transition-all">
              <h3 className="text-xl font-bold text-brand-navy mb-3">Commercial Cleaning Winnipeg</h3>
              <p className="text-gray-700">Professional commercial cleaning services for retail, office buildings, and commercial facilities across Winnipeg, Manitoba.</p>
            </div>
            
            <div className="bg-brand-blue-50 p-6 rounded-lg border-2 border-brand-blue-200 hover:border-brand-gold transition-all">
              <h3 className="text-xl font-bold text-brand-navy mb-3">Post Construction Clean Winnipeg</h3>
              <p className="text-gray-700">Specialized post-construction cleaning and debris removal for new builds and renovations throughout Manitoba.</p>
            </div>
            
            <div className="bg-brand-blue-50 p-6 rounded-lg border-2 border-brand-blue-200 hover:border-brand-gold transition-all">
              <h3 className="text-xl font-bold text-brand-navy mb-3">Waste Dump Winnipeg</h3>
              <p className="text-gray-700">Garbage pickup, waste disposal, and waste dump services for residential, commercial, and construction sites.</p>
            </div>
            
            <div className="bg-brand-blue-50 p-6 rounded-lg border-2 border-brand-blue-200 hover:border-brand-gold transition-all">
              <h3 className="text-xl font-bold text-brand-navy mb-3">Property Manager Winnipeg</h3>
              <p className="text-gray-700">Professional property management services including tenant relations, maintenance coordination, and property inspections.</p>
            </div>
            
            <div className="bg-brand-blue-50 p-6 rounded-lg border-2 border-brand-blue-200 hover:border-brand-gold transition-all">
              <h3 className="text-xl font-bold text-brand-navy mb-3">Live-In Caretaker Winnipeg</h3>
              <p className="text-gray-700">Experienced live-in caretaker services for residential properties, vacation homes, and rental properties across Manitoba.</p>
            </div>
            
            <div className="bg-brand-blue-50 p-6 rounded-lg border-2 border-brand-blue-200 hover:border-brand-gold transition-all">
              <h3 className="text-xl font-bold text-brand-navy mb-3">Clean Services Winnipeg</h3>
              <p className="text-gray-700">Complete cleaning services including residential, commercial, deep clean, carpet cleaning, and specialized cleaning solutions.</p>
            </div>
          </div>
          
          <div className="text-center mt-10">
            <p className="text-lg font-semibold text-brand-navy mb-2">
              Available 24/7 Throughout Winnipeg, Manitoba
            </p>
            <p className="text-gray-600">
              Call us today: <a href="tel:204-294-6802" className="text-brand-blue-600 hover:text-brand-gold font-bold">204-294-6802</a>
            </p>
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div key={service.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={`space-y-6 ${!isEven ? 'lg:order-2' : ''}`}>
                    <div className="inline-flex items-center space-x-3 bg-brand-blue-100 px-4 py-2 rounded-full">
                      <Icon className="h-5 w-5 text-brand-blue-600" />
                      <span className="text-sm font-semibold text-brand-blue-700">
                        {service.title}
                      </span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                      {service.title}
                    </h2>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {service.fullDescription}
                    </p>
                    <div className="space-y-3">
                      <h3 className="font-semibold text-gray-900 text-lg">What's Included:</h3>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {service.features.map((feature, idx) => (
                          <li key={`${service.id}-feature-${idx}`} className="flex items-start space-x-2">
                            <CheckCircle className="h-5 w-5 text-brand-blue-600 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Link to="/book">
                      <Button size="lg" className="bg-brand-blue-600 hover:bg-brand-blue-700 text-white mt-4">
                        Book This Service
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                  <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                    <Card className="overflow-hidden border-2 shadow-lg">
                      <div className="relative w-full h-80 bg-gray-100">
                        <img
                          src={service.imageUrl}
                          alt={service.title}
                          className="w-full h-full object-cover object-center"
                        />
                      </div>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Areas We Serve in Winnipeg
            </h2>
            <p className="text-lg text-gray-600">
              Proudly serving communities across Winnipeg, Manitoba
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              'Downtown Winnipeg', 'St. Vital', 'River Heights', 'Osborne Village',
              'Fort Garry', 'Transcona', 'Charleswood', 'St. James',
              'St. Boniface', 'Wolseley', 'Crescentwood', 'Tuxedo'
            ].map((area) => (
              <div key={area} className="bg-gray-50 p-4 rounded-lg text-center border border-gray-200 hover:border-brand-blue-500 hover:bg-brand-blue-50 transition-all duration-200">
                <p className="text-gray-700 font-medium">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-brand-blue-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Need a Custom Cleaning Plan?
          </h2>
          <p className="text-xl text-brand-blue-50 mb-8">
            We'll work with you to create a cleaning solution that fits your specific needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book">
              <Button size="lg" className="bg-white text-brand-blue-600 hover:bg-gray-100 w-full sm:w-auto">
                Request Quote
              </Button>
            </Link>
            <a href="tel:2042946802">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 w-full sm:w-auto">
                Call 204-294-6802
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
